import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../shared/api';
import { AiChartRenderer, type AiChartSpec } from './AiChartRenderer';

type AiResponse = {
    answer_short: string;
    answer_detail: string;
    charts: AiChartSpec[];
    sources: string[];
    confidence: number;
    warnings: string[];
};

type AiMessage =
    | { role: 'assistant'; answer_short: string; answer_detail: string; charts: AiChartSpec[]; warnings: string[]; sources: string[]; confidence: number }
    | { role: 'user'; content: string };

type FiltersPayload = {
    company_ids: number[];
    date_from: string;
    date_to: string;
};

/**
 * Filtriraj grafove koji ne nose informaciju:
 *  - prazni (0 točaka)
 *  - line grafovi s manje od 2 točke (nema linije, samo točka)
 *  - svi numerički ključevi su 0 ili null (chart bez signala)
 */
function chartsWithData(charts: AiChartSpec[]): AiChartSpec[] {
    return charts.filter((chart) => {
        const data = Array.isArray(chart.data) ? chart.data : [];
        if (data.length === 0) return false;
        if (chart.type === 'line' && data.length < 2) return false;
        const seriesKeys = (chart.series ?? []).map((s) => s.key);
        if (seriesKeys.length === 0) return false;
        // Postoji li bar jedna ne-nula vrijednost?
        const hasSignal = data.some((row) =>
            seriesKeys.some((k) => {
                const v = (row as Record<string, unknown>)[k];
                return typeof v === 'number' && Number.isFinite(v) && Math.abs(v) > 0.0000001;
            }),
        );
        return hasSignal;
    });
}

/** Welcome text odgovara modulima kojima korisnik ima pristup. Bez tehničkih izraza. */
function buildWelcomeMessage(userName: string, allowedModules: string[]): {
    short: string;
    detail: string;
    suggestions: string[];
} {
    // Per-modul: ljudski label + 1-2 primjera pitanja koja ima smisla pitati za taj modul.
    const moduleConfig: Record<string, { label: string; examples: string[] }> = {
        pnl: {
            label: 'financije i poslovanje',
            examples: ['Koliki nam je promet ovog mjeseca?', 'Tko su top 5 dobavljača?'],
        },
        ado: {
            label: 'ADO — ponude i prodaja zlata',
            examples: ['Koliko smo izdali ponuda ovog mjeseca?', 'Koja je konverzija ponuda u račune?'],
        },
        cashflow: {
            label: 'priljeve i odljeve po bankovnim računima',
            examples: ['Kakvo je stanje na računima?', 'Kolika je razlika priljeva i odljeva ovog mjeseca?'],
        },
        loans: {
            label: 'pozajmice i kredite',
            examples: ['Koje pozajmice su trenutno aktivne?'],
        },
        payroll: {
            label: 'plaće i stimulacije',
            examples: ['Koliko je ukupno plaća isplaćeno ovog mjeseca?'],
        },
        hedging: {
            label: 'zaštitu od volatilnosti cijene zlata',
            examples: ['Kakav nam je trenutno hedging snapshot?'],
        },
        bank_ready: {
            label: 'pripremu paketa za banku',
            examples: ['Što još fali u bank ready paketu?'],
        },
        documentation: {
            label: 'dokumente i ugovore',
            examples: ['Imamo li ugovor s tim partnerom?'],
        },
        warehouse: {
            label: 'skladišno poslovanje',
            examples: ['Koje proizvode imamo nisko na stanju?'],
        },
        warehouse_gold_cm: {
            label: 'Gold Category Manager — cijene i spread',
            examples: ['Kolika je trenutna spot cijena?', 'Koji kanal ima najveći spread?'],
        },
        warehouse_summary_orders: {
            label: 'zbirne naloge presklađištenja',
            examples: ['Koliko zbirnih naloga čeka obradu?'],
        },
        warehouse_stock: {
            label: 'stanje proizvoda na skladištu',
            examples: ['Što imamo nisko na stanju?', 'Koje rezervacije su aktivne?'],
        },
        monestiq: {
            label: 'Monestiq lokacije i transakcije',
            examples: ['Kakav je promet po lokacijama?'],
        },
        admin: { label: 'administraciju i korisnike', examples: [] },
        dashboard: { label: 'pregled cijelog sustava', examples: [] },
    };

    const firstName = (userName || '').split(/\s+/)[0] || '';
    const greeting = firstName ? `Bok ${firstName}!` : 'Bok!';

    const accessible = allowedModules
        .map((m) => moduleConfig[m])
        .filter((x): x is { label: string; examples: string[] } => Boolean(x));

    if (accessible.length === 0) {
        return {
            short: `${greeting} Još nemaš pristup modulima.`,
            detail: 'Javi se administratoru da ti dodijeli pristup, pa ćemo onda moći raditi zajedno.',
            suggestions: [],
        };
    }

    const labels = accessible.map((x) => x.label);
    const suggestions: string[] = [];

    // Ljudski oblikuj listu modula: "X, Y i Z" umjesto "X, Y, Z".
    const niceList =
        labels.length === 1
            ? labels[0]
            : labels.length === 2
                ? `${labels[0]} i ${labels[1]}`
                : `${labels.slice(0, -1).join(', ')} i ${labels[labels.length - 1]}`;

    return {
        short: `${greeting} Mogu ti pomoći s temom: ${niceList}.`,
        detail: 'Pitaj me običnim jezikom — odgovorit ću s brojkama, grafom i kratkim komentarom.',
        suggestions,
    };
}

export function AiChatBubble({
    token,
    module,
    filters,
    allowedModules,
    userName = '',
    isSuperadmin = false,
}: {
    token: string;
    module: string;
    filters: FiltersPayload;
    allowedModules: string[];
    userName?: string;
    isSuperadmin?: boolean;
}) {
    const [open, setOpen] = useState(false);
    const [showAnalytics, setShowAnalytics] = useState(false);
    const [question, setQuestion] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const bottomRef = useRef<HTMLDivElement | null>(null);
    const welcome = useMemo(() => buildWelcomeMessage(userName, allowedModules), [userName, allowedModules]);
    const [messages, setMessages] = useState<AiMessage[]>([
        {
            role: 'assistant',
            answer_short: welcome.short,
            answer_detail: welcome.detail,
            charts: [],
            warnings: [],
            sources: [],
            confidence: 1,
        },
    ]);

    const latestFilters = useMemo(() => ({
        company_ids: filters.company_ids,
        date_from: filters.date_from,
        date_to: filters.date_to,
    }), [filters.company_ids, filters.date_from, filters.date_to]);

    useEffect(() => {
        if (!open) {
            return;
        }
        bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, [messages, loading, open]);

    async function submit() {
        const trimmed = question.trim();
        if (!trimmed || loading) {
            return;
        }
        setLoading(true);
        setError('');
        setMessages((current) => [...current, { role: 'user', content: trimmed }]);
        setQuestion('');

        try {
            const response = await api<AiResponse>('/ai/chat', token, {
                method: 'POST',
                body: JSON.stringify({
                    question: trimmed,
                    module,
                    filters: latestFilters,
                }),
            });
            setMessages((current) => [
                ...current,
                {
                    role: 'assistant',
                    answer_short: response.answer_short,
                    answer_detail: response.answer_detail,
                    charts: response.charts ?? [],
                    warnings: response.warnings ?? [],
                    sources: response.sources ?? [],
                    confidence: response.confidence ?? 0.5,
                },
            ]);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'AI upit nije uspio.');
        } finally {
            setLoading(false);
        }
    }

    return (
        <>
            <button className="ai-chat-fab" type="button" onClick={() => setOpen((v) => !v)}>
                AI
            </button>
            {open ? (
                <aside className="ai-chat-panel card">
                    <div className="ai-chat-head">
                        <strong>BI AI Assistant</strong>
                        <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                            {isSuperadmin ? (
                                <button className="secondary compact" type="button" onClick={() => setShowAnalytics(true)} title="Anonimizirana analitika pitanja (što ljude zanima + neodgovorena)">📊</button>
                            ) : null}
                            <button className="secondary compact" type="button" onClick={() => setOpen(false)}>Zatvori</button>
                        </span>
                    </div>
                    <div className="ai-chat-body">
                        {messages.map((message, index) => (
                            message.role === 'user'
                                ? <p className="ai-msg-user" key={`u-${index}`}>{message.content}</p>
                                : (
                                    <article className="ai-msg-assistant" key={`a-${index}`}>
                                        <p><strong>{message.answer_short}</strong></p>
                                        {message.answer_detail ? <p className="muted">{message.answer_detail}</p> : null}
                                        {message.warnings.length > 0 ? <p className="error">{message.warnings.join(' ')}</p> : null}
                                        <AiChartRenderer charts={chartsWithData(message.charts)} />
                                    </article>
                                )
                        ))}
                        <div ref={bottomRef} />
                    </div>
                    <div className="ai-chat-input-row">
                        <input
                            type="text"
                            placeholder="Pitaj me običnim jezikom…"
                            value={question}
                            onChange={(event) => setQuestion(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    event.preventDefault();
                                    void submit();
                                }
                            }}
                            disabled={loading}
                        />
                        <button type="button" onClick={() => void submit()} disabled={loading || question.trim() === ''}>
                            {loading ? '...' : 'Pošalji'}
                        </button>
                    </div>
                    {/* Predefined quick-row gumbovi su uklonjeni — user piše svoje pitanje. */}
                    {error ? <p className="error">{error}</p> : null}
                </aside>
            ) : null}
            {showAnalytics && isSuperadmin ? <AiQuestionAnalytics token={token} onClose={() => setShowAnalytics(false)} /> : null}
        </>
    );
}

/* Anonimizirana analitika AI pitanja (superadmin). Bez identiteta — agregati. */
function AiQuestionAnalytics({ token, onClose }: { token: string; onClose: () => void }) {
    const [data, setData] = useState<any>(null);
    const [days, setDays] = useState(90);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        setLoading(true);
        api<any>(`/ai/analytics?days=${days}`, token).then(setData).catch(() => setData(null)).finally(() => setLoading(false));
    }, [token, days]);
    const totals = data?.totals;
    const answeredPct = totals && totals.total > 0 ? Math.round(100 * (totals.answered_cnt ?? 0) / totals.total) : 0;
    return (
        <div role="dialog" aria-modal="true" onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(820px,96vw)', maxHeight: '90vh', overflowY: 'auto', background: '#fff', borderRadius: 12, padding: 0 }}>
                <div style={{ background: 'linear-gradient(135deg,#1e40af,#3b82f6)', color: '#fff', padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 10, position: 'sticky', top: 0 }}>
                    <strong>📊 AI pitanja — anonimizirano</strong>
                    <select value={days} onChange={(e) => setDays(Number(e.target.value))} style={{ marginLeft: 8, fontSize: '0.8rem' }}>
                        <option value={30}>30 dana</option><option value={90}>90 dana</option><option value={365}>godina</option>
                    </select>
                    <button type="button" onClick={onClose} style={{ marginLeft: 'auto', background: 'rgba(255,255,255,0.2)', color: '#fff', border: 0, borderRadius: 6, padding: '4px 12px', cursor: 'pointer' }}>✕</button>
                </div>
                <div style={{ padding: 18 }}>
                    <p style={{ fontSize: '0.78rem', color: '#888', marginTop: 0 }}>Ne bilježimo TKO je pitao — samo što se pita i je li dobiveno odgovoreno. Cilj: vidjeti što ljude zanima, posebno gdje AI nema odgovor.</p>
                    {loading ? <p className="muted">Učitavam…</p> : !data ? <p className="error">Greška.</p> : (
                        <>
                            {totals ? (
                                <div style={{ display: 'flex', gap: 14, marginBottom: 16, flexWrap: 'wrap' }}>
                                    <Stat label="Pitanja" value={String(totals.total ?? 0)} />
                                    <Stat label="Odgovoreno" value={`${answeredPct}%`} color={answeredPct >= 70 ? '#1f7a36' : '#b14040'} />
                                    <Stat label="Prosj. confidence" value={`${totals.avg_conf ?? 0}%`} />
                                </div>
                            ) : null}

                            <h4 style={{ margin: '12px 0 6px', color: '#b14040' }}>❓ Neodgovorena pitanja (najčešća)</h4>
                            {(data.unanswered ?? []).length === 0 ? <p className="muted" style={{ fontSize: '0.82rem' }}>Nema neodgovorenih — bravo.</p> : (
                                <div className="table-wrap" style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid #f0d0d0', borderRadius: 6 }}>
                                    <table className="admin-user-table" style={{ fontSize: '0.78rem' }}>
                                        <thead><tr><th>Pitanje</th><th>Modul</th><th style={{ textAlign: 'right' }}>×</th><th style={{ textAlign: 'right' }}>conf</th></tr></thead>
                                        <tbody>{(data.unanswered ?? []).map((r: any, i: number) => (
                                            <tr key={i}><td>{r.question_excerpt}</td><td style={{ fontSize: '0.72rem' }}>{r.module}</td><td style={{ textAlign: 'right', fontWeight: 700 }}>{r.cnt}</td><td style={{ textAlign: 'right', color: '#b14040' }}>{r.conf}%</td></tr>
                                        ))}</tbody>
                                    </table>
                                </div>
                            )}

                            <h4 style={{ margin: '16px 0 6px', color: '#1e40af' }}>🔝 Najčešća pitanja</h4>
                            <div className="table-wrap" style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 6 }}>
                                <table className="admin-user-table" style={{ fontSize: '0.78rem' }}>
                                    <thead><tr><th>Pitanje</th><th style={{ textAlign: 'right' }}>×</th><th style={{ textAlign: 'right' }}>odg.</th><th style={{ textAlign: 'right' }}>conf</th></tr></thead>
                                    <tbody>{(data.top ?? []).map((r: any, i: number) => (
                                        <tr key={i}><td>{r.question_excerpt}</td><td style={{ textAlign: 'right', fontWeight: 700 }}>{r.cnt}</td><td style={{ textAlign: 'right' }}>{r.ans}/{r.cnt}</td><td style={{ textAlign: 'right' }}>{r.conf}%</td></tr>
                                    ))}</tbody>
                                </table>
                            </div>

                            <h4 style={{ margin: '16px 0 6px', color: 'var(--ad-ink)' }}>📦 Po modulu</h4>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                {(data.by_module ?? []).map((r: any, i: number) => (
                                    <span key={i} style={{ fontSize: '0.76rem', background: 'rgba(var(--ad-soft-rgb),0.12)', padding: '3px 9px', borderRadius: 10 }}>{r.module}: <strong>{r.cnt}</strong> ({r.ans} odg.)</span>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function Stat({ label, value, color }: { label: string; value: string; color?: string }) {
    return (
        <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 14px', minWidth: 110 }}>
            <div style={{ fontSize: '0.66rem', textTransform: 'uppercase', color: '#888' }}>{label}</div>
            <strong style={{ fontSize: '1.15rem', color: color ?? '#1e40af' }}>{value}</strong>
        </div>
    );
}
